import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../Button';
import AppIcon from '../icons/AppIcon';
import { useDeclineCall, useJoinCall } from '../../hooks/useCallsQuery';
import useRingtone from '../../hooks/useRingtone';
import useUserMutes from '../../hooks/useUserMutes';

/**
 * Architecture Rule 74 — Full-screen incoming-call modal triggered by
 * the CALL_RINGING WebSocket event (see `useIncomingCall`). Answer →
 * POST /calls/{id}/join → navigate to /call/{id}. Decline → POST
 * /calls/{id}/decline → close modal.
 *
 * Browser Notification API integration: if the page is hidden when a
 * call comes in, fire a `new Notification(...)` so the user gets a
 * background ring. Requires user-granted permission (we request it on
 * first mount of the modal).
 *
 * Auto-close at `ringingExpiresAt` is handled by the parent hook —
 * this component just renders.
 */
export default function IncomingCallModal({ incoming, onClose }) {
  const { t } = useTranslation();
  const joinCall = useJoinCall();
  const declineCall = useDeclineCall();
  const navigate = useNavigate();

  // Rule 74 v1.68 — start the ringtone while the modal is mounted, stop on
  // answer/decline/close. May be silently muted by the browser's auto-play
  // policy on first ring after a cold tab load; the browser Notification API
  // below covers the always-fires path.
  //
  // Per-user mute gating: if the auth user has muted the caller (started_by),
  // we suppress the ringtone but still show the modal so they CAN answer if
  // they want — mute is ring-only, not block-and-hide.
  const { isMuted } = useUserMutes();
  const callerIsMuted = incoming?.startedBy ? isMuted(incoming.startedBy) : false;
  useRingtone(Boolean(incoming) && !callerIsMuted);

  // Browser Notification — only when the tab is in the background.
  useEffect(() => {
    if (!incoming) return;
    if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
    if (document.visibilityState === 'visible') return;

    const fire = () => {
      try {
        // eslint-disable-next-line no-new
        new Notification(t('calls.incomingNotification'), {
          body: incoming.kind === 'video' ? t('calls.ringingVideo') : t('calls.ringingVoice'),
          tag: `call-${incoming.callId}`,
        });
      } catch (_) { /* notification API can throw in odd contexts; safe to ignore */ }
    };

    if (Notification.permission === 'granted') {
      fire();
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((p) => { if (p === 'granted') fire(); });
    }
  }, [incoming, t]);

  if (!incoming) return null;

  const handleAnswer = async () => {
    try {
      await joinCall.mutateAsync(incoming.callId);
      onClose?.();
      navigate(`/call/${incoming.callId}`);
    } catch (err) {
      toast.error(err?.friendlyMessage ?? t('calls.failedToAnswer'));
    }
  };

  const handleDecline = async () => {
    try {
      await declineCall.mutateAsync(incoming.callId);
    } catch (_) { /* swallow — closing the modal is the user's intent */ }
    onClose?.();
  };

  const kindLabel = incoming.kind === 'video' ? t('calls.kindVideo') : t('calls.kindVoice');
  // Caller display name — full_name from the WS payload, fallback to username,
  // then to "User #N" as a last-resort label. Matches the same priority order
  // used by mapMessageRow and CallSummaryCard so the SPA shows a consistent
  // name everywhere a caller appears.
  const callerDisplay = incoming.startedByName
    || incoming.startedByUsername
    || `User #${incoming.startedBy}`;
  // v1.76 — initial-letter fallback when the caller has no avatar. Matches
  // the chat sidebar's avatar style for visual continuity.
  const callerInitial = callerDisplay.trim().charAt(0).toUpperCase() || '?';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('calls.incomingAria')}
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
      }}
    >
      <div
        className="detail-panel"
        style={{
          background: 'var(--surface, #fff)',
          color: 'var(--text, #1f2937)',
          borderRadius: 'var(--radius, 5px)',
          padding: '2rem',
          minWidth: '24rem',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
          <AppIcon name={incoming.kind === 'video' ? 'videoCall' : 'voiceCall'} size={40} />
        </div>
        <p style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #6b7280)' }}>
          {t('calls.incomingHeader', { kind: incoming.kind })}
        </p>

        {/* v1.76 — caller avatar above the name. Falls back to initial-letter
            badge when the caller has no profile photo. 96px is large enough to
            read across the room (modal ringer UX) but small enough to keep the
            CTA buttons in view on a 1366×768 laptop. */}
        <div
          aria-hidden="true"
          style={{
            margin: '1rem auto 0',
            width: '5.5rem',
            height: '5.5rem',
            borderRadius: '50%',
            overflow: 'hidden',
            background: 'var(--accent-soft, #e0e7ff)',
            color: 'var(--accent, #4338ca)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.25rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {incoming.startedByProfilePhotoUrl
            ? (
              <img
                src={incoming.startedByProfilePhotoUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )
            : callerInitial}
        </div>

        <p style={{ fontSize: '1.5rem', fontWeight: 600, marginTop: '0.75rem' }}>
          {t('calls.incomingTitle', { startedBy: callerDisplay })}
        </p>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted, #6b7280)', marginTop: '0.25rem' }}>
          {t('calls.kindThread', { kindLabel, threadId: incoming.threadId })}
        </p>

        <div
          style={{
            marginTop: '1.5rem',
            display: 'flex',
            justifyContent: 'center',
            gap: '0.75rem',
          }}
        >
          <Button
            variant="danger"
            size="lg"
            onClick={handleDecline}
            disabled={declineCall.isPending}
            aria-label={t('calls.declineAria')}
          >
            {t('calls.decline')}
          </Button>
          <Button
            variant="success"
            size="lg"
            onClick={handleAnswer}
            disabled={joinCall.isPending}
            aria-label={t('calls.answerAria')}
          >
            {t('calls.answer')}
          </Button>
        </div>
      </div>
    </div>
  );
}
